import { useMemo, useState } from "react";
import type { Lang } from "../types.ts";
import type { LoadedCatalog } from "../catalog.ts";
import type { ProgressMap } from "../state/store.ts";
import { DEFAULT_CLASS_ID } from "../exam.ts";
import { t } from "../i18n/ui.ts";

interface Props {
  catalog: LoadedCatalog;
  lang: Lang;
  progress: ProgressMap;
  onExam: (setId: number) => void;
  onPractice: (setId: number) => void;
}

/** The official exam sheets, pickable one by one. */
export function SheetsView({ catalog, lang, progress, onExam, onPractice }: Props) {
  const [openId, setOpenId] = useState<number | null>(null);

  const sheets = useMemo(
    () =>
      catalog.sets
        .filter((s) => s.classId === DEFAULT_CLASS_ID)
        .sort((a, b) => a.rank - b.rank),
    [catalog.sets]
  );

  return (
    <div className="sheets">
      <h2 className="sheets__title">{t(lang, "sheetsTitle")}</h2>
      <p className="sheets__hint">{t(lang, "sheetsHint")}</p>

      <ul className="sheets__grid">
        {sheets.map((sheet) => {
          const correct = sheet.questionIds.filter(
            (id) => progress[String(id)]?.lastCorrect === true
          ).length;
          const open = openId === sheet.id;

          return (
            <li key={sheet.id} className={`sheet ${open ? "sheet--open" : ""}`}>
              <button
                type="button"
                className="sheet__btn"
                aria-expanded={open}
                onClick={() => setOpenId(open ? null : sheet.id)}
              >
                <span className="sheet__name">
                  {t(lang, "sheetLabel", { n: sheet.rank })}
                </span>
                <span className="sheet__score">
                  {t(lang, "sheetDone", {
                    correct, total: sheet.questionIds.length,
                  })}
                </span>
                <span className="sheet__bar" aria-hidden="true">
                  <span
                    className="sheet__fill"
                    style={{ inlineSize: `${(correct / sheet.questionIds.length) * 100}%` }}
                  />
                </span>
              </button>

              {open && (
                <div className="sheet__actions">
                  <button type="button" className="btn" onClick={() => onPractice(sheet.id)}>
                    {t(lang, "sheetPractice")}
                  </button>
                  <button
                    type="button"
                    className="btn btn--primary"
                    onClick={() => onExam(sheet.id)}
                  >
                    {t(lang, "sheetExam")}
                  </button>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
